import gsap from 'gsap';
import clamp from '../utils/clamp';
import cover from '../utils/cover';
import isTailwindBreakpoint from '../utils/isTailwindBreakpoint';

type MediaItemMetrics = {
  element: HTMLElement;
  index: number;
  top: number;
  itemHeight: number;
};

export default class ProductGallery extends HTMLElement {
  private mediasContainer: HTMLUListElement | null = null;
  private mediasScrollbar: HTMLElement | null = null;
  private mediaItems: MediaItemMetrics[] = [];
  private nav: HTMLElement | null = null;
  private navButtons: HTMLButtonElement[] = [];
  private zoomTriggers: HTMLButtonElement[] = [];
  private zoomOverlay: HTMLDialogElement | null = null;
  private zoomOverlayContent: HTMLDivElement | null = null;
  private zoomOverlayCloseButton: HTMLButtonElement | null = null;
  private galleryCursor: HTMLDivElement | null = null;
  private zoomCursor: HTMLDivElement | null = null;
  private triggerElement: HTMLElement | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onUpdateBound = this.onUpdate.bind(this);
  private onMediaScrollBound = this.onMediaScroll.bind(this);
  private onNavScrollBound = this.onNavScroll.bind(this);
  private onNavButtonClickBound = this.onNavButtonClick.bind(this);
  private onUserScrollInterruptBound = this.onUserScrollInterrupt.bind(this);
  private onZoomTriggerClickBound = this.onZoomTriggerClick.bind(this);
  private onZoomOverlayClickBound = this.onZoomOverlayClick.bind(this);
  private onZoomOverlayCloseButtonClickBound = this.onZoomOverlayCloseButtonClick.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onMouseEnterBound = this.onMouseEnter.bind(this);
  private onMouseLeaveBound = this.onMouseLeave.bind(this);
  private onElementMouseMoveBound = this.onElementMouseMove.bind(this);
  private onMouseMoveBound = this.onMouseMove.bind(this);
  private onTouchStartBound = this.onTouchStart.bind(this);
  private onTouchMoveBound = this.onTouchMove.bind(this);
  private onTouchEndBound = this.onTouchEnd.bind(this);
  private showCurrentMediaBound = this.showCurrentMedia.bind(this);

  private mediasWidth: number = 0;
  private mediasScrollBarWidth: number = 0;
  private mediasScrollWidth: number = 0;
  private mediasScrollLeft: number = 0;

  private rafId: number | null = null;
  private isBelowLg: boolean = false;
  private desktopNavListenersAttached = false;

  private isSnapping = false;
  private snapTween: gsap.core.Tween | null = null;
  private snapProxy = { y: 0 };
  private currentItemIndex = 0;
  private navTargetIndex: number | null = null;

  private readonly snapEase = 'power3.out';
  private readonly snapThreshold = 3;
  private readonly snapDurationMin = 0.6;
  private readonly snapDurationMax = 1.2;

  private isOpen: boolean = false;
  private isTouch: boolean = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  private ease = 0.085;

  private pointerX: number = 0;
  private pointerY: number = 0;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private normalizedPointerX: number = 0;
  private normalizedPointerY: number = 0;

  private initialDistance: number = 0;
  private initialScale: number = 1;
  private currentScale: number = 1;
  private targetScale: number = 1;
  private minScale: number = 1;
  private maxScale: number = 3;

  private lastTapTime: number = 0;
  private doubleTapDelay: number = 300;
  private lastTapX: number = 0;
  private lastTapY: number = 0;
  private doubleTapThreshold: number = 50;

  private showZoomOverlayCloseButtonTl: gsap.core.Timeline | null = null;
  private hideZoomOverlayCloseButtonTl: gsap.core.Timeline | null = null;

  private cursorIsLeaving: boolean = false;
  private cursorIsShown: boolean = false;
  private showCursorTl: gsap.core.Timeline | null = null;
  private hideCursorTl: gsap.core.Timeline | null = null;

  private showZoomOverlayTl: gsap.core.Timeline | null = null;
  private hideZoomOverlayTl: gsap.core.Timeline | null = null;
  private showCurrentMediaTl: gsap.core.Timeline | null = null;

  private mobileOffsetX: number = 0;
  private mobileOffsetY: number = 0;
  private targetZoomTranslateX: number = 0.5;
  private targetZoomTranslateY: number = 0.5;
  private currentZoomTranslateX: number = 0.5;
  private currentZoomTranslateY: number = 0.5;

  private translateXPotential: number = 0;
  private translateYPotential: number = 0;

  private cursorState = { rotation: 0 };

  private currentMedia: HTMLImageElement | null = null;
  private currentMediaWidth: number = 0;
  private currentMediaHeight: number = 0;

  connectedCallback(): void {
    this.mediasContainer = this.querySelector('[data-product-gallery-ref="medias-container"]');
    this.mediasScrollbar = this.querySelector('[data-product-gallery-ref="medias-scrollbar"]');
    this.nav = this.querySelector('[data-product-gallery-ref="nav"]');
    this.navButtons = Array.from(this.querySelectorAll('[data-product-gallery-ref="nav-buttons"]'));
    this.zoomTriggers = Array.from(this.querySelectorAll('[data-product-gallery-ref="zoom-trigger"]'));
    this.zoomOverlay = this.querySelector('[data-product-gallery-ref="zoom-overlay"]');
    this.zoomOverlayContent = this.querySelector('[data-product-gallery-ref="zoom-overlay-content"]');
    this.zoomOverlayCloseButton = this.querySelector('[data-product-gallery-ref="zoom-overlay-close-button"]');
    this.galleryCursor = this.querySelector('[data-product-gallery-ref="cursor"]');
    this.zoomCursor = this.querySelector('[data-product-gallery-ref="zoom-cursor"]');

    this.addEventListeners();
    this.rafId = requestAnimationFrame(this.onUpdateBound);
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);
    window.addEventListener('mousemove', this.onMouseMoveBound);
    window.addEventListener('touchstart', this.onTouchStartBound);
    window.addEventListener('touchmove', this.onTouchMoveBound, { passive: false });
    window.addEventListener('touchend', this.onTouchEndBound);

    if (this.mediasContainer) {
      this.mediasContainer.addEventListener('mouseenter', this.onMouseEnterBound);
      this.mediasContainer.addEventListener('mouseleave', this.onMouseLeaveBound);
      this.mediasContainer.addEventListener('mousemove', this.onElementMouseMoveBound);
    }

    this.zoomTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onZoomTriggerClickBound);
    });

    this.navButtons.forEach((button) => {
      button.addEventListener('click', this.onNavButtonClickBound);
    });

    this.nav?.addEventListener('scroll', this.onNavScrollBound, { passive: true });

    if (this.zoomOverlay) {
      this.zoomOverlay.addEventListener('click', this.onZoomOverlayClickBound);
      this.zoomOverlay.addEventListener('cancel', this.onDialogCancelBound);
      this.zoomOverlay.addEventListener('close', this.onDialogCloseBound);
    }

    if (this.zoomOverlayCloseButton) {
      this.zoomOverlayCloseButton.addEventListener('click', this.onZoomOverlayCloseButtonClickBound);
    }

    this.onResize();
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);
    window.removeEventListener('mousemove', this.onMouseMoveBound);
    window.removeEventListener('touchstart', this.onTouchStartBound);
    window.removeEventListener('touchmove', this.onTouchMoveBound);
    window.removeEventListener('touchend', this.onTouchEndBound);

    if (this.mediasContainer) {
      this.mediasContainer.removeEventListener('mouseenter', this.onMouseEnterBound);
      this.mediasContainer.removeEventListener('mouseleave', this.onMouseLeaveBound);
      this.mediasContainer.removeEventListener('mousemove', this.onElementMouseMoveBound);
    }

    this.zoomTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onZoomTriggerClickBound);
    });

    this.navButtons.forEach((button) => {
      button.removeEventListener('click', this.onNavButtonClickBound);
    });

    this.nav?.removeEventListener('scroll', this.onNavScrollBound);

    this.detachDesktopNavListeners();

    if (this.zoomOverlay) {
      this.zoomOverlay.removeEventListener('click', this.onZoomOverlayClickBound);
      this.zoomOverlay.removeEventListener('cancel', this.onDialogCancelBound);
      this.zoomOverlay.removeEventListener('close', this.onDialogCloseBound);
    }

    if (this.zoomOverlayCloseButton) {
      this.zoomOverlayCloseButton.removeEventListener('click', this.onZoomOverlayCloseButtonClickBound);
    }

    if (this.mediasContainer) {
      this.mediasContainer.removeEventListener('scroll', this.onMediaScrollBound);
    }

    if (this.currentMedia) {
      this.currentMedia.removeEventListener('load', this.showCurrentMediaBound);
    }

    this.cancelSnap();

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private attachDesktopNavListeners(): void {
    if (this.desktopNavListenersAttached) {
      return;
    }

    window.addEventListener('wheel', this.onUserScrollInterruptBound, { passive: true });
    window.addEventListener('touchstart', this.onUserScrollInterruptBound, { passive: true });
    this.desktopNavListenersAttached = true;
  }

  private detachDesktopNavListeners(): void {
    if (!this.desktopNavListenersAttached) {
      return;
    }

    window.removeEventListener('wheel', this.onUserScrollInterruptBound);
    window.removeEventListener('touchstart', this.onUserScrollInterruptBound);
    this.desktopNavListenersAttached = false;
  }

  onResize(): void {
    if (this.zoomOverlay) {
      this.zoomOverlay.style.width = `${window.innerWidth}px`;
      this.zoomOverlay.style.height = `${window.innerHeight}px`;
    }

    this.measureMediaItems();

    if (this.mediasContainer) {
      this.mediasWidth = this.mediasContainer.getBoundingClientRect().width;
      this.mediasScrollWidth = this.mediasContainer.scrollWidth;
    }

    if (this.mediasScrollbar) {
      this.mediasScrollBarWidth = this.mediasScrollbar.getBoundingClientRect().width;
    }

    const isBelowLg = !isTailwindBreakpoint('lg');

    if (this.mediasContainer) {
      if (isBelowLg && !this.isBelowLg) {
        this.mediasContainer.addEventListener('scroll', this.onMediaScrollBound);
        this.detachDesktopNavListeners();
        this.cancelSnap();
      } else if (!isBelowLg && this.isBelowLg) {
        this.mediasContainer.removeEventListener('scroll', this.onMediaScrollBound);
        this.attachDesktopNavListeners();
        this.currentItemIndex = this.getCurrentItemIndex(window.scrollY);
        this.updateNavButtons(this.currentItemIndex);
      }
    }

    this.isBelowLg = isBelowLg;

    if (!this.isBelowLg) {
      const wasAttached = this.desktopNavListenersAttached;
      this.attachDesktopNavListeners();

      if (!wasAttached) {
        this.currentItemIndex = this.getCurrentItemIndex(window.scrollY);
        this.updateNavButtons(this.currentItemIndex);
      }

      this.updateNavScrollMasks();
    }
  }

  private measureMediaItems(): void {
    if (!this.mediasContainer) {
      this.mediaItems = [];
      return;
    }

    const scrollY = window.scrollY;

    this.mediaItems = Array.from(this.mediasContainer.children).map((element, index) => {
      const item = element as HTMLElement;

      return {
        element: item,
        index,
        top: item.getBoundingClientRect().top + scrollY,
        itemHeight: item.offsetHeight,
      };
    });
  }

  onUpdate(): void {
    this.rafId = requestAnimationFrame(this.onUpdateBound);

    if (!this.isBelowLg) {
      this.updateDesktopNavIndex();
    }

    if (this.isBelowLg && this.mediasContainer && this.mediasContainer.scrollWidth > 0 && this.mediasScrollbar) {
      const maxScrollLeft = this.mediasScrollWidth - this.mediasWidth;
      const maxTranslateX = this.mediasWidth - this.mediasScrollBarWidth;

      if (maxScrollLeft > 0 && maxTranslateX > 0) {
        const progress = this.mediasScrollLeft / maxScrollLeft;
        const clampedProgress = Math.min(Math.max(progress, 0), 1);
        const translateX = (clampedProgress * maxTranslateX).toFixed(2);
        this.mediasScrollbar.style.transform = `translate3d(${translateX}px, 0, 0)`;
      } else {
        this.mediasScrollbar.style.transform = '';
      }
    }

    this.currentZoomTranslateX += (this.targetZoomTranslateX - this.currentZoomTranslateX) * this.ease;
    this.currentZoomTranslateY += (this.targetZoomTranslateY - this.currentZoomTranslateY) * this.ease;
    this.currentScale += (this.targetScale - this.currentScale) * this.ease;

    if (this.currentMedia) {
      this.currentMedia.style.transform = `translate3d(-${this.currentZoomTranslateX}px, -${this.currentZoomTranslateY}px, 0)`;
    }

    if (this.zoomOverlayContent) {
      this.zoomOverlayContent.style.transform = `scale(${this.currentScale})`;
    }

    const cursorTransform = `translate3d(${this.pointerX}px, ${this.pointerY}px, 0) rotate(${this.cursorState.rotation}deg)`;

    if (this.galleryCursor) {
      this.galleryCursor.style.transform = cursorTransform;
    }

    if (this.zoomCursor) {
      this.zoomCursor.style.transform = cursorTransform;
    }
  }

  private updateDesktopNavIndex(): void {
    const scrollY = window.scrollY;
    let index: number;

    if (this.navTargetIndex !== null) {
      const targetItem = this.mediaItems[this.navTargetIndex];
      const reachedTarget = targetItem && Math.abs(scrollY - targetItem.top) < this.snapThreshold;

      if (reachedTarget && !this.isSnapping) {
        this.navTargetIndex = null;
        index = this.getCurrentItemIndex(scrollY);
      } else {
        index = this.navTargetIndex;
      }
    } else {
      index = this.getCurrentItemIndex(scrollY);
    }

    if (index !== this.currentItemIndex) {
      this.currentItemIndex = index;
      this.updateNavButtons(index);
    }
  }

  onMediaScroll(): void {
    if (this.mediasContainer) {
      this.mediasScrollLeft = this.mediasContainer.scrollLeft;
    }
  }

  private onNavScroll(): void {
    this.updateNavScrollMasks();
  }

  private onNavButtonClick(event: MouseEvent): void {
    if (this.isBelowLg) {
      return;
    }

    const button = event.currentTarget as HTMLButtonElement;
    const index = parseInt(button.dataset.index ?? '0', 10);
    const item = this.mediaItems[index];

    if (!item) {
      return;
    }

    if (index === this.currentItemIndex) {
      return;
    }

    this.navTargetIndex = index;
    this.currentItemIndex = index;
    this.updateNavButtons(index);
    this.animateScrollTo(item.top);
  }

  private animateScrollTo(targetY: number): void {
    const scrollY = window.scrollY;

    if (Math.abs(targetY - scrollY) < this.snapThreshold) {
      return;
    }

    this.cancelSnap();

    this.isSnapping = true;
    this.snapProxy.y = scrollY;

    const distance = Math.abs(targetY - scrollY);
    const duration = this.getSnapDuration(distance);

    this.snapTween = gsap.to(this.snapProxy, {
      y: targetY,
      duration,
      ease: this.snapEase,
      onUpdate: () => {
        window.scrollTo(0, this.snapProxy.y);
      },
      onComplete: () => {
        this.isSnapping = false;
        this.snapTween = null;
      },
    });
  }

  private cancelSnap(): void {
    if (this.snapTween) {
      this.snapTween.kill();
      this.snapTween = null;
    }

    this.isSnapping = false;
  }

  private onUserScrollInterrupt(): void {
    if (!this.isSnapping) {
      return;
    }

    this.navTargetIndex = null;
    this.cancelSnap();
  }

  private getSnapDuration(distance: number): number {
    const vh = window.innerHeight;
    const duration = (distance / vh) * 1.1;

    return Math.min(this.snapDurationMax, Math.max(this.snapDurationMin, duration));
  }

  private getCurrentItemIndex(scrollY: number): number {
    const viewportMiddle = scrollY + window.innerHeight / 2;
    let index = 0;

    for (const item of this.mediaItems) {
      if (item.top <= viewportMiddle) {
        index = item.index;
      } else {
        break;
      }
    }

    return index;
  }

  private updateNavButtons(index: number): void {
    this.navButtons.forEach((button) => {
      const buttonIndex = parseInt(button.dataset.index ?? '0', 10);
      const isActive = buttonIndex === index;

      button.classList.toggle('border-charcoal', isActive);
      button.classList.toggle('border-transparent', !isActive);

      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    this.scrollNavToIndex(index);
    this.updateNavScrollMasks();
  }

  private scrollNavToIndex(index: number): void {
    if (!this.nav) {
      return;
    }

    const button = this.navButtons[index];
    const listItem = button?.parentElement;

    if (!listItem) {
      return;
    }

    gsap.to(this.nav, {
      scrollTop: listItem.offsetTop - 16,
      duration: 0.6,
      ease: 'power3.out',
    });
  }

  private updateNavScrollMasks(): void {
    if (!this.nav) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = this.nav;
    const threshold = 1;
    const canScrollUp = scrollTop > threshold;
    const canScrollDown = scrollTop + clientHeight < scrollHeight - threshold;

    this.nav.classList.toggle('scroll-hero-nav-mask-top', canScrollUp && !canScrollDown);
    this.nav.classList.toggle('scroll-hero-nav-mask-bottom', canScrollDown && !canScrollUp);
    this.nav.classList.toggle('scroll-hero-nav-mask-both', canScrollUp && canScrollDown);
  }

  onMouseEnter(): void {
    this.cursorIsLeaving = false;
    this.showCursor();
  }

  onMouseLeave(): void {
    this.cursorIsLeaving = true;
    this.hideCursor();
  }

  onMouseMove(event: MouseEvent): void {
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;

    this.normalizedPointerX = 1 - (this.pointerX / window.innerWidth) * 2;
    this.normalizedPointerY = 1 - (this.pointerY / window.innerHeight) * 2;

    if (this.isTouch === false) {
      this.targetZoomTranslateX = this.getZoomTranslateX(this.normalizedPointerX);
      this.targetZoomTranslateY = this.getZoomTranslateY(this.normalizedPointerY * -1);
    }
  }

  onElementMouseMove(): void {
    if (this.cursorIsShown === false && this.cursorIsLeaving === false) {
      this.showCursor();
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (this.isOpen && event.touches.length > 0) {
      const touch = event.touches[0];
      this.pointerX = touch.clientX;
      this.pointerY = touch.clientY;

      this.dragStartX = this.pointerX - this.mobileOffsetX;
      this.dragStartY = this.pointerY - this.mobileOffsetY;

      if (event.touches.length === 2) {
        event.preventDefault();
        this.initialDistance = this.getDistance(event.touches[0], event.touches[1]);
        this.initialScale = this.currentScale;
      }
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (this.isOpen && event.touches.length > 0) {
      if (event.touches.length === 2) {
        event.preventDefault();
        const currentDistance = this.getDistance(event.touches[0], event.touches[1]);
        const scale = (currentDistance / this.initialDistance) * this.initialScale;
        this.targetScale = clamp(scale, this.minScale, this.maxScale);

        this.mobileOffsetX = 0;
        this.mobileOffsetY = 0;
        this.targetZoomTranslateX = this.getMobileZoomTranslateX(this.mobileOffsetX);
        this.targetZoomTranslateY = this.getMobileZoomTranslateY(this.mobileOffsetY);
      } else if (event.touches.length === 1) {
        const touch = event.touches[0];
        this.pointerX = touch.clientX;
        this.pointerY = touch.clientY;

        const offsetX = this.pointerX - this.dragStartX;
        const offsetY = this.pointerY - this.dragStartY;

        this.mobileOffsetX = offsetX;
        this.mobileOffsetY = offsetY;

        this.targetZoomTranslateX = this.getMobileZoomTranslateX(this.mobileOffsetX);
        this.targetZoomTranslateY = this.getMobileZoomTranslateY(this.mobileOffsetY);
      }
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.isOpen && event.changedTouches.length > 0) {
      const touch = event.changedTouches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;

      const now = Date.now();
      const timeSinceLastTap = now - this.lastTapTime;
      const dx = touchX - this.lastTapX;
      const dy = touchY - this.lastTapY;

      if (
        timeSinceLastTap < this.doubleTapDelay &&
        Math.abs(dx) < this.doubleTapThreshold &&
        Math.abs(dy) < this.doubleTapThreshold
      ) {
        this.targetScale = 1;
        this.mobileOffsetX = 0;
        this.mobileOffsetY = 0;
        this.targetZoomTranslateX = this.getMobileZoomTranslateX(this.mobileOffsetX);
        this.targetZoomTranslateY = this.getMobileZoomTranslateY(this.mobileOffsetY);
      } else {
        this.lastTapTime = now;
        this.lastTapX = touchX;
        this.lastTapY = touchY;
      }
    }
  }

  getMobileZoomTranslateX(offsetX: number): number {
    this.translateXPotential = (this.currentMediaWidth * this.currentScale - window.innerWidth) / 2;

    const offset = clamp(
      offsetX / this.currentScale,
      -this.translateXPotential / this.currentScale,
      this.translateXPotential / this.currentScale,
    );

    return this.currentMediaWidth / 2 - offset;
  }

  getMobileZoomTranslateY(offsetY: number): number {
    this.translateYPotential = (this.currentMediaHeight * this.currentScale - window.innerHeight) / 2;

    const offset = clamp(
      offsetY / this.currentScale,
      -this.translateYPotential / this.currentScale,
      this.translateYPotential / this.currentScale,
    );

    return this.currentMediaHeight / 2 - offset;
  }

  getZoomTranslateX(offsetX: number): number {
    this.translateXPotential = (this.currentMediaWidth - window.innerWidth) / 2;
    return this.currentMediaWidth / 2 + this.translateXPotential * offsetX;
  }

  getZoomTranslateY(offsetY: number): number {
    this.translateYPotential = (this.currentMediaHeight - window.innerHeight) / 2;
    return this.currentMediaHeight / 2 + this.translateYPotential * offsetY;
  }

  getDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  showZoomOverlayCloseButton(): void {
    if (this.hideZoomOverlayCloseButtonTl) {
      this.hideZoomOverlayCloseButtonTl.kill();
    }
    if (this.showZoomOverlayCloseButtonTl) {
      this.showZoomOverlayCloseButtonTl.kill();
    }

    if (this.isTouch === false || !this.zoomOverlayCloseButton) {
      return;
    }

    this.showZoomOverlayCloseButtonTl = gsap.timeline();
    this.showZoomOverlayCloseButtonTl.to(
      this.zoomOverlayCloseButton,
      {
        autoAlpha: 1,
        duration: 0.4,
        ease: 'power2.out',
      },
      0,
    );
  }

  hideZoomOverlayCloseButton(): void {
    if (this.showZoomOverlayCloseButtonTl) {
      this.showZoomOverlayCloseButtonTl.kill();
    }
    if (this.hideZoomOverlayCloseButtonTl) {
      this.hideZoomOverlayCloseButtonTl.kill();
    }

    if (this.isTouch === false || !this.zoomOverlayCloseButton) {
      return;
    }

    this.hideZoomOverlayCloseButtonTl = gsap.timeline();
    this.hideZoomOverlayCloseButtonTl.to(
      this.zoomOverlayCloseButton,
      {
        autoAlpha: 0,
        duration: 0.2,
        ease: 'power2.out',
      },
      0,
    );
  }

  syncZoomOverlayCloseButtonA11y(): void {
    if (!this.zoomOverlayCloseButton) {
      return;
    }

    if (this.isTouch) {
      this.zoomOverlayCloseButton.removeAttribute('tabindex');
      this.zoomOverlayCloseButton.removeAttribute('aria-hidden');
      return;
    }

    this.zoomOverlayCloseButton.setAttribute('tabindex', '-1');
    this.zoomOverlayCloseButton.setAttribute('aria-hidden', 'true');
  }

  showCursor(): void {
    if (this.isOpen) {
      return;
    }

    this.cursorIsShown = true;

    if (this.hideCursorTl) {
      this.hideCursorTl.kill();
    }
    if (this.showCursorTl) {
      this.showCursorTl.kill();
    }

    this.showCursorTl = gsap.timeline();

    if (this.isTouch === false && this.galleryCursor) {
      this.showCursorTl.to(
        this.galleryCursor,
        {
          duration: 0,
          autoAlpha: 1,
          ease: 'linear',
        },
        0,
      );
    }
  }

  hideCursor(): void {
    this.cursorIsShown = false;
    this.cursorIsLeaving = true;

    if (this.showCursorTl) {
      this.showCursorTl.kill();
    }
    if (this.hideCursorTl) {
      this.hideCursorTl.kill();
    }

    this.hideCursorTl = gsap.timeline();

    if (this.isTouch === false && this.galleryCursor) {
      this.hideCursorTl.to(
        this.galleryCursor,
        {
          duration: 0,
          autoAlpha: 0,
          ease: 'linear',
        },
        0,
      );
    }
  }

  showZoomCursor(): void {
    if (this.isTouch || !this.zoomCursor) {
      return;
    }

    gsap.set(this.zoomCursor, { autoAlpha: 1 });
  }

  hideZoomCursor(): void {
    if (!this.zoomCursor) {
      return;
    }

    gsap.set(this.zoomCursor, { autoAlpha: 0 });
  }

  restoreGalleryCursorIfNeeded(): void {
    this.hideZoomCursor();

    if (this.cursorIsShown && !this.cursorIsLeaving) {
      this.showCursor();
    }
  }

  onZoomTriggerClick(event: Event): void {
    const trigger = event.currentTarget as HTMLButtonElement;
    const media = trigger.querySelector('img');

    if (!(media instanceof HTMLImageElement)) {
      return;
    }

    this.triggerElement = trigger;

    this.currentMedia = media.cloneNode(true) as HTMLImageElement;
    this.currentMedia.sizes = '100vw';
    this.currentMedia.setAttribute('class', '');
    this.currentMedia.setAttribute('aria-hidden', 'true');

    gsap.set(this.currentMedia, {
      autoAlpha: 0,
      position: 'absolute',
      top: '50%',
      left: '50%',
      maxWidth: 'none',
      maxHeight: 'none',
      pointerEvents: 'none',
    });

    if (this.zoomOverlayContent) {
      this.zoomOverlayContent.innerHTML = '';
      this.zoomOverlayContent.appendChild(this.currentMedia);
      this.showZoomOverlay();
    }
  }

  onZoomOverlayClick(event: MouseEvent): void {
    if (!this.isOpen || this.isTouch) {
      return;
    }

    if (this.zoomOverlayCloseButton?.contains(event.target as Node)) {
      return;
    }

    this.hideZoomOverlay();
  }

  onZoomOverlayCloseButtonClick(): void {
    if (this.isOpen) {
      this.hideZoomOverlay();
    }
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    this.hideZoomOverlay();
  }

  onDialogClose(): void {
    this.isOpen = false;
    gsap.set([document.documentElement, document.body], { clearProps: 'overflow' });
    this.restoreGalleryCursorIfNeeded();
    this.restoreFocus();
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  showZoomOverlay(): void {
    if (!this.zoomOverlay) {
      return;
    }

    this.isOpen = true;

    gsap.set([document.documentElement, document.body], { overflow: 'hidden' });

    this.targetScale = 1;
    this.currentScale = 1;
    this.mobileOffsetX = 0;
    this.mobileOffsetY = 0;

    if (this.hideZoomOverlayTl) {
      this.hideZoomOverlayTl.kill();
    }
    if (this.showZoomOverlayTl) {
      this.showZoomOverlayTl.kill();
    }

    gsap.set(this.zoomOverlay, { autoAlpha: 0, visibility: 'visible' });

    this.syncZoomOverlayCloseButtonA11y();

    if (!this.zoomOverlay.open) {
      this.zoomOverlay.showModal();
    }

    if (this.galleryCursor) {
      gsap.set(this.galleryCursor, { autoAlpha: 0 });
    }
    this.showZoomCursor();

    this.showZoomOverlayTl = gsap.timeline();

    this.showZoomOverlayTl.to(
      this.cursorState,
      {
        rotation: this.cursorState.rotation + 45,
        duration: 0.6,
        ease: 'expo.out',
      },
      0,
    );
    this.showZoomOverlayTl.to(
      this.zoomOverlay,
      {
        autoAlpha: 1,
        duration: 0.4,
        ease: 'power2.inOut',
      },
      0,
    );
    this.showZoomOverlayTl.add(() => {
      if (this.currentMedia) {
        if (this.currentMedia.complete) {
          this.showCurrentMedia();
        } else {
          this.currentMedia.addEventListener('load', this.showCurrentMediaBound);
        }
      }
    }, 0.4);
    this.showZoomOverlayTl.add(() => {
      this.showZoomOverlayCloseButton();
    }, 0.4);
  }

  hideZoomOverlay(): void {
    if (!this.zoomOverlay) {
      return;
    }

    this.isOpen = false;

    if (this.showZoomOverlayTl) {
      this.showZoomOverlayTl.kill();
    }
    if (this.hideZoomOverlayTl) {
      this.hideZoomOverlayTl.kill();
    }

    if (this.currentMedia) {
      this.currentMedia.removeEventListener('load', this.showCurrentMediaBound);
    }

    this.hideZoomOverlayTl = gsap.timeline({
      onComplete: () => {
        this.restoreGalleryCursorIfNeeded();

        if (this.zoomOverlay?.open) {
          this.zoomOverlay.close();
        }

        if (this.zoomOverlayContent) {
          this.zoomOverlayContent.innerHTML = '';
        }

        this.currentMedia = null;
      },
    });

    this.hideZoomOverlayTl.add(() => {
      this.hideZoomOverlayCloseButton();
    }, 0);
    this.hideZoomOverlayTl.to(
      this.cursorState,
      {
        rotation: this.cursorState.rotation + 45,
        duration: 0.4,
        ease: 'expo.out',
      },
      0,
    );
    this.hideZoomOverlayTl.to(
      this.zoomOverlay,
      {
        autoAlpha: 0,
        duration: 0.2,
        ease: 'power2.inOut',
      },
      0,
    );
  }

  showCurrentMedia(): void {
    if (this.showCurrentMediaTl) {
      this.showCurrentMediaTl.kill();
    }

    if (this.currentMedia) {
      const naturalWidth = this.currentMedia.naturalWidth;
      const naturalHeight = this.currentMedia.naturalHeight;

      const currentMediaCoverSize = cover(
        {
          width: naturalWidth,
          height: naturalHeight,
        },
        {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      );

      this.currentMediaWidth = currentMediaCoverSize.width;
      this.currentMediaHeight = currentMediaCoverSize.height;

      gsap.set(this.currentMedia, {
        width: `${this.currentMediaWidth}px`,
        height: `${this.currentMediaHeight}px`,
      });

      if (this.isTouch) {
        this.targetZoomTranslateX = this.getMobileZoomTranslateX(this.mobileOffsetX);
        this.targetZoomTranslateY = this.getMobileZoomTranslateY(this.mobileOffsetY);
      } else {
        this.targetZoomTranslateX = this.getZoomTranslateX(this.normalizedPointerX);
        this.targetZoomTranslateY = this.getZoomTranslateY(this.normalizedPointerY * -1);
      }

      this.currentZoomTranslateX = this.targetZoomTranslateX;
      this.currentZoomTranslateY = this.targetZoomTranslateY;
    }

    this.showCurrentMediaTl = gsap.timeline();

    if (this.currentMedia) {
      this.showCurrentMediaTl.to(
        this.currentMedia,
        {
          autoAlpha: 1,
          duration: 0.4,
          ease: 'power2.out',
        },
        0,
      );
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    gsap.set([document.documentElement, document.body], { clearProps: 'overflow' });
  }
}

customElements.define('product-gallery', ProductGallery);
